import { useState, useRef } from 'react';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function SubmitDuckForm() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const caption = (form.elements.namedItem('caption') as HTMLTextAreaElement).value;
    if (caption.length > 500) return;

    setStatus('submitting');

    const data = new URLSearchParams();
    const fields = ['name', 'email', 'location', 'caption', 'instagram'];
    fields.forEach((field) => {
      const el = form.elements.namedItem(field) as HTMLInputElement | HTMLTextAreaElement | null;
      if (el) data.append(field, el.value);
    });
    data.append('photo', 'Uncollectable');

    try {
      const res = await fetch('https://readdy.ai/api/form/d6oopeuancq2eie822a0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        formRef.current?.reset();
        setCharCount(0);
        setPreviewUrl(null);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="py-20 bg-white" id="submit">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block bg-pink-100 text-pink-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            Share Your Find
          </span>
          <h2 className="text-4xl font-extrabold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Found a Duck? Tell Us! 🦆
          </h2>
          <p className="text-gray-500 text-base leading-relaxed">
            Submit your duck discovery and we'll feature it in our community gallery. Every find is a little moment of joy worth celebrating!
          </p>
        </div>

        {status === 'success' ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-10 text-center">
            <div className="text-5xl mb-4">🦆</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Duck Submitted!</h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Thank you for sharing your find! We'll review it and add it to the gallery soon. Don't forget to post on social with <strong>#WhatTheDuck</strong>!
            </p>
            <button
              onClick={() => setStatus('idle')}
              className="px-6 py-2.5 bg-pink-600 text-white rounded-full text-sm font-semibold hover:bg-pink-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Submit Another Find
            </button>
          </div>
        ) : (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            data-readdy-form
            className="bg-gray-50 border border-gray-200 rounded-2xl p-8 space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Your Name <span className="text-pink-500">*</span></label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="e.g. Sarah M."
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-pink-300 transition"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email <span className="text-pink-500">*</span></label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="you@email.com"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-pink-300 transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Where Did You Find It? <span className="text-pink-500">*</span></label>
              <input
                type="text"
                name="location"
                required
                placeholder="e.g. Sugar Hill Community Park, Suwanee Town Center..."
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-pink-300 transition"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Your Duck Story <span className="text-pink-500">*</span></label>
              <textarea
                name="caption"
                required
                rows={4}
                maxLength={500}
                placeholder="Tell us about finding your duck! How did it make you feel? Did your kids react? Share the moment! 🦆"
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-pink-300 transition resize-none"
              />
              <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-rose-500' : 'text-gray-400'}`}>
                {charCount}/500
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Upload Your Photo</label>
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-5 bg-white text-center hover:border-pink-300 transition-colors cursor-pointer">
                <input
                  type="file"
                  name="photo"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="duck-photo"
                />
                <label htmlFor="duck-photo" className="cursor-pointer">
                  {previewUrl ? (
                    <div className="flex flex-col items-center gap-3">
                      <img src={previewUrl} alt="Preview" className="w-32 h-32 object-cover rounded-xl" />
                      <span className="text-xs text-pink-600 font-semibold">Click to change photo</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 py-4">
                      <div className="w-10 h-10 flex items-center justify-center text-gray-400">
                        <i className="ri-image-add-line text-3xl"></i>
                      </div>
                      <p className="text-sm text-gray-500">Click to upload your duck photo</p>
                      <p className="text-xs text-gray-400">JPG, PNG, HEIC up to 10MB</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Instagram Handle <span className="text-gray-400 font-normal">(optional)</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
                <input
                  type="text"
                  name="instagram"
                  placeholder="yourusername"
                  className="w-full border border-gray-200 rounded-lg pl-8 pr-4 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-pink-300 transition"
                />
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
              <span className="text-xl mt-0.5">🦆</span>
              <p className="text-xs text-yellow-800 leading-relaxed">
                By submitting, you give The Mommy Mingle permission to feature your photo and story in our community gallery. We'll always credit you!
              </p>
            </div>

            {status === 'error' && (
              <p className="text-rose-500 text-sm text-center">Something went wrong. Please try again!</p>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full py-3.5 bg-pink-600 text-white font-bold rounded-full hover:bg-pink-700 transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === 'submitting' ? 'Submitting...' : '🦆 Submit My Duck Find'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
